import { useEffect, RefObject, useRef } from 'react';

/**
 * Hook to manage focus when modal opens/closes
 * - Sets initial focus on the title element (if titleId provided) or modal when opened
 * - Returns focus to previous active element when closed
 * @param isOpen - Whether the modal is currently open
 * @param modalRef - Ref to the modal element
 * @param titleId - Optional ID of the title element to focus on open
 */
export function useModalFocus(
  isOpen: boolean,
  modalRef: RefObject<HTMLDivElement>,
  titleId?: string,
) {
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const openFocusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeFocusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (closeFocusTimeoutRef.current !== null) {
      clearTimeout(closeFocusTimeoutRef.current);
      closeFocusTimeoutRef.current = null;
      // The cancelled close timeout would have cleared this ref; do it now
      // so the current trigger element is recaptured below.
      previousActiveElementRef.current = null;
    }

    if (!previousActiveElementRef.current) {
      previousActiveElementRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }

    openFocusTimeoutRef.current = setTimeout(() => {
      const focusTarget = titleId ? (document.getElementById(titleId) as HTMLElement | null) : null;
      (focusTarget ?? modalRef.current)?.focus();
    }, 0);

    return () => {
      if (openFocusTimeoutRef.current !== null) {
        clearTimeout(openFocusTimeoutRef.current);
        openFocusTimeoutRef.current = null;
      }

      if (previousActiveElementRef.current) {
        closeFocusTimeoutRef.current = setTimeout(() => {
          previousActiveElementRef.current?.focus();
          previousActiveElementRef.current = null;
        }, 0);
      }
    };
  }, [isOpen, modalRef, titleId]);
}
